import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import useAuthStore from "../../Zustand/Store/useAuthStore";
import usePermissionDataStore from "../../Zustand/Store/usePermissionDataStore";
import { fetchExternalApplications } from "../../Apis/Wallet-api";
import { formatMoney } from "../../Utils/walletFormat";
import { hasPermissionAction } from "../../Utils/organizationPermissions";
import { isWalletAgentUser, isWalletClientUser } from "../../Utils/walletAdminAccess";

const ALL_QUICK_LINKS = [
  { label: "External applications", path: "/wallet/applications", action: "WALLET_APPLICATIONS_VIEW" },
  { label: "Transactions", path: "/wallet/transactions", action: "WALLET_TRANSACTIONS_VIEW" },
  { label: "Ledger", path: "/wallet/ledger", action: "WALLET_LEDGER_VIEW" },
  { label: "Credit management", path: "/wallet/credit", action: "WALLET_CREDIT_VIEW" },
  { label: "Integration", path: "/wallet/integration", action: "WALLET_INTEGRATION_VIEW" },
  { label: "Manual adjustment", path: "/wallet/manual-adjustment", action: "WALLET_MANUAL_ADJUSTMENT" },
  { label: "Notifications", path: "/wallet/notifications", action: "WALLET_NOTIFICATIONS_VIEW" },
  { label: "White label", path: "/wallet/white-label", action: "WALLET_WHITE_LABEL_MANAGE" },
];

export default function Dashboard() {
  const navigate = useNavigate();
  const { userData } = useAuthStore();
  const { Permission } = usePermissionDataStore();
  const isClient = isWalletClientUser(userData);
  const isAgent = isWalletAgentUser(userData);

  const clientName =
    userData?.wallet_agent?.name ||
    userData?.wallet_client?.client_name ||
    null;

  const orgName =
    clientName ||
    userData?.organization?.organization_name ||
    userData?.user_active_organization?.organization?.organization_name ||
    "your organization";

  const quickLinks = useMemo(
    () =>
      ALL_QUICK_LINKS.filter((link) =>
        hasPermissionAction(Permission, link.action)
      ),
    [Permission]
  );

  const [loading, setLoading] = useState(true);
  const [apps, setApps] = useState([]);

  useEffect(() => {
    fetchExternalApplications()
      .then((rows) => setApps(Array.isArray(rows) ? rows : []))
      .catch(() => setApps([]))
      .finally(() => setLoading(false));
  }, []);

  const totalBalance = apps.reduce((sum, a) => sum + Number(a.balance || 0), 0);
  const totalCreditUsed = apps.reduce((sum, a) => sum + Number(a.credit_used || 0), 0);

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 960 }}>
      <Typography variant="h4" gutterBottom>
        {isAgent
          ? "Your agent overview"
          : isClient
            ? "Your wallet overview"
            : "Wallet operations"}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        {isAgent
          ? `Signed in as ${clientName || "your agent"}. Summary of balance and credit for your linked agent.`
          : isClient
            ? `Signed in as ${orgName}. Summary of your agents, balances, and credit usage.`
            : `Signed in for ${orgName}. Internal admin console for agents, balances, and access controls.`}
      </Typography>

      {loading ? (
        <CircularProgress size={28} />
      ) : (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={4}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">
                {isClient ? "Your agents" : "External agents"}
              </Typography>
              <Typography variant="h5">{apps.length}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Total balance
              </Typography>
              <Typography variant="h5">{formatMoney(totalBalance)}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Total credit used
              </Typography>
              <Typography variant="h5">{formatMoney(totalCreditUsed)}</Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Modules
      </Typography>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {quickLinks.map((link) => (
          <Button
            key={link.path}
            variant="outlined"
            size="small"
            onClick={() => navigate(link.path)}
          >
            {link.label}
          </Button>
        ))}
        <Button variant="outlined" size="small" onClick={() => navigate("/my-profile")}>
          Profile
        </Button>
        <Button
          variant="outlined"
          size="small"
          onClick={() => navigate("/settings/change-password")}
        >
          Change password
        </Button>
      </Stack>
    </Box>
  );
}
